import React, {
    useState,
    useMemo,
    useEffect,
    ReactElement,
} from 'react';
import WeekSelect from 'shared/components/WeekSelect';
import moment from 'moment';
import {Modal, Button, notification, Select, Popover, DatePicker} from 'antd';
import {UpOutlined, DownOutlined} from '@ant-design/icons';
import PeriodTag from 'shared/components/PeriodTag';
import {gql} from 'apollo-boost';
import {useQuery, useMutation} from '@apollo/react-hooks';
import NavigationPrompt from 'shared/components/NavigationPrompt';
import logoIcon from 'shared/images/logo_white.svg';
import logoText from 'shared/images/text_white.svg';

import './MenuClient.scss';
import {
    WeekData,
    ClientData,
    Data,
    MealMenu,
    Meal,
    EditedMeal,
    DishType,
    ClientIdentity,
    Dish,
} from 'shared/views/seniorCommand/types';
import {
    getDefaultDishes,
    getMealsAlerts,
    areAllMealsEmpty,
    getWeekDaysArray,
    MealAlert,
} from 'shared/views/seniorCommand/functions';
import {INCREMENTABLE_DISH_TYPES} from 'shared/views/seniorCommand/constants';
import LoaderOverlay from 'shared/components/LoaderOverlay';
import ArrayService from 'shared/services/ArrayService';
import AlertTag from 'shared/components/AlertTag';
import { useLocation, useHistory } from 'react-router-dom';
import { showMailSendingError } from 'shared/services/FormService';

const GET_WEEK_DATA = gql`
    query getWeekData($startDate: Date, $endDate: Date) {
        mealMenus(
            clientTypes: ["senior"]
            startDate: $startDate
            endDate: $endDate
        ) {
            id
            date
            dishes {
                id
                dish {
                    id
                    name
                    type
                    allergies {
                        id
                        content
                    }
                    noSideDish
                }
                choiceNumber
                sortOrder
            }
            dishTypes
            mealDefinition {
                id
                mealPeriods
                name
                singleDish
            }
        }
    }
`;

const GET_CLIENT_DATA = gql`
    query getClientData($startDate: Date, $endDate: Date, $clientId: Int) {
        client(id: $clientId) {
            id
            firstName
            lastName
            defaultDeliveryZone {
                id
            }
            menuWeekClients {
                id
                week {
                    id
                    weekNumber
                    year
                }
                customerEditAt
            }
            clientIdentities {
                id
                firstname
                lastname
                isActive
                allergies {
                    id
                    content
                }
                diets {
                    id
                    content
                }
                defaultMeals {
                    id
                    weekDay
                    mealPeriod
                    meal {
                        id
                    }
                    deliveryDay
                    clientIdentityDefaultMealContents {
                        id
                        defaultMeal {
                            id
                        }
                        dish {
                            id
                        }
                        dishType
                        quantity
                    }
                }
                defaultDishes {
                    weekDay
                    mealPeriod
                    dishes {
                        dish {
                            id
                        }
                        quantity
                    }
                }
                allowMultipleDishes
            }
        }

        clientIdentityAbsences(
            from: $startDate
            to: $endDate
            clientId: $clientId
        ) {
            id
            date
            clientIdentity {
                id
            }
        }

        meals(from: $startDate, to: $endDate, clientId: $clientId) {
            id
            dishes {
                id
                dish {
                    id
                }
                quantity
                dishType
            }
            price
            menu {
                id
            }
            command {
                id
                status
                client {
                    id
                }
            }
            mealPeriod
            clientIdentity {
                id
            }
            date
            day
            orderNumber
        }
    }
`;

const GET_DATA = gql`
    query {
        dishTypes {
            code
            label
        }

        specialDiets {
            id
            content
        }
    }
`;

const USER_CLIENT_SUPPLEMENT = gql`
    query getData($userId: Int!) {
        user(id: $userId) {
            id
            clientSupplements {
                dish {
                    id
                    name
                    type
                    allergies {
                        id
                        content
                    }
                    noSideDish
                }
            }
        }
    }
`;

const CREATE_COMMAND = gql`
    mutation createCommand($input: CommandInput!) {
        createCommand(input: $input) {
            id
            status
        }
    }
`;

const ORDER_COMMAND = gql`
    mutation orderCommand($id: UUID!) {
        order(id: $id) {
            id
            status
        }
    }
`;

const SAVE_MEALS = gql`
    mutation saveMeals($command: UUID!, $meals: [MealInput!]!) {
        saveMeals(meals: $meals, command: $command) {
            id
            dishes {
                id
                dish {
                    id
                }
                quantity
            }
            price
            menu {
                id
            }
            command {
                id
                status
                client {
                    id
                }
            }
            mealPeriod
            clientIdentity {
                id
            }
            date
            orderNumber
        }
    }
`;

const SEND_EMAIL_MENU_VALIDATION = gql`
    mutation sendEmailMenuValidation($id: Int!, $week: Int!, $year: Int!){
        sendEmailMenuValidation(id: $id, week: $week, year: $year) {
            id
        }
    }
`;

const UPDATE_MENU_WEEK_CLIENT = gql`
    mutation updateMenuWeekClient($id: Int!, $input: MenuWeekClientInput!) {
        updateMenuWeekClient(id: $id, input: $input) {
            id
        }
    }
`;

const CREATE_MEAL_DISH_CLIENT = gql`
    mutation createMealDishClient($menuWeek: Int!, $startDate: Date!, $endDate: Date!, $clientId: Int!) {
        createMealDishClient(menuWeek: $menuWeek, from: $startDate, to: $endDate, clientId: $clientId) {
            id
        }
    }
`;

const WEEK_DAY_MAP: { [day: string]: number } = {
    monday: 1,
    tuesday: 2,
    wednesday: 3,
    thursday: 4,
    friday: 5,
    saturday: 6,
    sunday: 0,
};

export default function MenuClient () {

    const location = useLocation();
    const searchParams = new URLSearchParams(location.search);

    const customerParam = searchParams.get('customer');
    const tokenParam = searchParams.get('token');
    const weekSelected = searchParams.get('week');
    const yearSelected = searchParams.get('year');

    const clientId = customerParam ? parseInt(customerParam, 10) : null;

    const [selectedClientId, setSelectedClientId] =
        useState<string | null>(null);
    const [selectedClientIdentityId, setSelectedClientIdentityId] =
        useState<string | null>(null);
    const [selectedOrderNumber, setMealOrderNumber] =
        useState<number | null>(null);
    const [authError, setAuthError] = useState<string | null>(null);

    const history = useHistory ();

// 🧭 Calcule le lundi de la semaine sélectionnée, ou le lundi de la semaine prochaine par défaut
    const [firstWeekDay, setFirstWeekDay] = useState(
        weekSelected
            ? moment()
                .year(yearSelected)
                .week(parseInt(weekSelected, 10))
                .startOf('week')
                .add(1, 'day') // lundi (car startOf('week') = dimanche)
                .format('YYYY-MM-DD')
            : moment()
                .clone()
                .weekday(1) // lundi de la semaine courante
                .format('YYYY-MM-DD')
    );

    const [lastWeekDay, setLastWeekDay] = useState(
        weekSelected
            ? moment()
                .year(yearSelected)
                .week(parseInt(weekSelected, 10))
                .startOf('week')
                .add(7, 'day') // lundi (car startOf('week') = dimanche)
                .format('YYYY-MM-DD')
            : moment()
                .clone()
                .weekday(7) // lundi de la semaine courante
                .format('YYYY-MM-DD')
    );

    const [saveDraftLoading, setSaveDraftLoading] = useState(false);
    const [saveCommandLoading, setSaveCommandLoading] = useState(false);

    const [editedMeals, setEditedMeals] = useState<EditedMeal[]>([]);

    const filteredEditedMeals = editedMeals.filter(meal => meal.default != true);
    const navigationDisabled = filteredEditedMeals.length > 0;

    const {data: dataClientSupplement, refetch: refetchClientSupplement} = useQuery(USER_CLIENT_SUPPLEMENT, {
        variables: {userId: clientId},
        context: {
            headers: {
                'X-Menu-Route': 'menu-client',
                Authorization: tokenParam ? `Bearer ${tokenParam}` : '',
            },
        },
        skip: !clientId,
        onError: (err) => {
            console.error('GraphQL error:', err);
            // On récupère le message Symfony
            const message =
                err?.networkError?.result?.error ||
                err?.message ||
                'Erreur de connexion au serveur.';
            setAuthError(message);
        },
    });

    const weekDays = useMemo(
        () => getWeekDaysArray(firstWeekDay),
        [firstWeekDay],
    );
    const weekDaysStr = weekDays.map((d) => moment(d).format('YYYY-MM-DD'));

    const startDate: any = weekDaysStr.length > 0 ? weekDaysStr[0] : null;
    const endDate: any =
        weekDaysStr.length > 0 ? weekDaysStr[weekDaysStr.length - 1] : null;

    const {data: data, error: error} = useQuery<Data>(GET_DATA, {
        context: {
            headers: {
                Authorization: tokenParam ? `Bearer ${tokenParam}` : '',
            },
        },
        fetchPolicy: 'cache-and-network',
    });
    const [localWeekData, setLocalWeekData] = useState<WeekData | null>(null);
    const {data: weekData, error: weekDataError} = useQuery<WeekData>(
        GET_WEEK_DATA,
        {
            variables: {
                startDate,
                endDate,
            },
            context: {
                headers: {
                    Authorization: tokenParam ? `Bearer ${tokenParam}` : '',
                },
            },
            fetchPolicy: 'cache-and-network',
            skip: !tokenParam,
            onError: (err) => {
                console.error('GraphQL error:', err);
                // On récupère le message Symfony
                const message =
                    err?.networkError?.result?.error ||
                    err?.message ||
                    'Erreur de connexion au serveur.';
                setAuthError(message);
            },
        },
    );

    const {
        data: clientData,
        loading: clientDataLoading,
        error: clientDataError,
        refetch: refetchClientData,
    } = useQuery<ClientData>(GET_CLIENT_DATA, {
        variables: {
            startDate,
            endDate,
            clientId: clientId,
        },
        context: {
            headers: {
                Authorization: tokenParam ? `Bearer ${tokenParam}` : '',
            },
        },
        fetchPolicy: 'network-only',
        skip: !clientId,
        onError: (err) => {
            console.error('GraphQL error:', err);
            // On récupère le message Symfony
            const message =
                err?.networkError?.result?.error ||
                err?.message ||
                'Erreur de connexion au serveur.';
            setAuthError(message);
        },
    });

    const [createCommand] = useMutation(CREATE_COMMAND);
    const [orderCommand] = useMutation(ORDER_COMMAND);
    const [saveMeals] = useMutation(SAVE_MEALS);
    const [updateMenuWeekClient] = useMutation(UPDATE_MENU_WEEK_CLIENT);
    const [createMealDishClient] = useMutation(CREATE_MEAL_DISH_CLIENT);
    const [sendEmailMenuValidation] = useMutation(SEND_EMAIL_MENU_VALIDATION, {
        onError: (err) => {
            showMailSendingError(err);
        },
    });

    const weekNumber = moment(firstWeekDay).week();
    const year = moment(firstWeekDay).year();
    const newYear = moment(lastWeekDay).year();

    const menuWeekClientInfos= clientData?.client?.menuWeekClients?.find(
        (weekClient: any) => weekClient.week.weekNumber === weekNumber && weekClient.week.year === year
    );

    function getFrenchHolidays(year: number): string[] {
        // Jour de Pâques (basé sur le calcul de la date de Pâques)
        const easter = moment(calculateEaster(year));

        return [
            moment(`${year}-01-01`).format("YYYY-MM-DD"), // Nouvel An
            //easter.add(1, "days").format("YYYY-MM-DD"),  // Lundi de Pâques
            moment(`${year}-05-01`).format("YYYY-MM-DD"), // Fête du Travail
            moment(`${year}-05-08`).format("YYYY-MM-DD"), // Victoire 1945
            easter.add(38, "days").format("YYYY-MM-DD"), // Ascension
            easter.add(49, "days").format("YYYY-MM-DD"), // Pentecôte
            easter.add(50, "days").format("YYYY-MM-DD"), // Lundi de Pentecôte
            moment(`${year}-07-14`).format("YYYY-MM-DD"), // Fête Nationale
            moment(`${year}-08-15`).format("YYYY-MM-DD"), // Assomption
            moment(`${year}-11-01`).format("YYYY-MM-DD"), // Toussaint
            moment(`${year}-11-11`).format("YYYY-MM-DD"), // Armistice 1918
            moment(`${year}-12-25`).format("YYYY-MM-DD"), // Noël
        ];
    }

    // Fonction pour calculer la date de Pâques (algorithme de Gauss simplifié)
    function calculateEaster(year: number): string {
        const a = year % 19;
        const b = Math.floor(year / 100);
        const c = year % 100;
        const d = Math.floor(b / 4);
        const e = b % 4;
        const f = Math.floor((b + 8) / 25);
        const g = Math.floor((b - f + 1) / 3);
        const h = (19 * a + b - d - g + 15) % 30;
        const i = Math.floor(c / 4);
        const k = c % 4;
        const l = (32 + 2 * e + 2 * i - h - k) % 7;
        const m = Math.floor((a + 11 * h + 22 * l) / 451);
        const month = Math.floor((h + l - 7 * m + 114) / 31);
        const day = ((h + l - 7 * m + 114) % 31) + 1;

        return `${year}-${month}-${day}`;
    }

    const commands = useMemo(
        () =>
            clientData
                ? clientData.meals
                    .map((m: any) => m.command)
                    .filter(ArrayService.filterUniqueEntity)
                : [],
        [clientData],
    );

    useEffect(() => {
        if(clientData){
            if (menuWeekClientInfos?.customerEditAt != null) {
                history.push({
                    pathname: `/menu-client-used`,
                });
            }
        }
    }, [
        clientData,
        history
    ]);

    useEffect(() => {
        if (data) {
            setSelectedClientId(
                clientData ? clientData.client.id + '' : null,
            );

            const clientIdentityId = clientData && clientData.client.clientIdentities.find((i: ClientIdentity) => i.isActive);

            setSelectedClientIdentityId(
                clientIdentityId ? clientIdentityId.id : null,
            );

            setMealOrderNumber(selectedOrderNumber ? selectedOrderNumber : 1);
        }
        if(clientData){
            if (menuWeekClientInfos?.customerEditAt != null) {
                history.push({
                    pathname: `/menu-client-used`,
                });
            }
        }
    }, [
        data,
        history,
        selectedClientId,
        selectedClientIdentityId,
        selectedOrderNumber
    ]);

    const currentClient: any = clientData ? clientData.client : null;
    const currentClientIdentity: ClientIdentity = currentClient && currentClient.clientIdentities.find((i: ClientIdentity) => i.isActive);

    const absenceDates: any = useMemo(
        () =>
            clientData
                ? clientData.clientIdentityAbsences
                    .filter(
                        (a: any) =>
                            currentClientIdentity &&
                            a.clientIdentity.id === currentClientIdentity.id,
                    )
                    .map((a: any) => moment(a.date))
                : [],
        [clientData, currentClientIdentity],
    );

    useEffect(() => {
            if (
                saveDraftLoading ||
                saveCommandLoading ||
                !clientData ||
                !weekData ||
                !currentClient
            ) {
                return;
            }

            const clientMeals = clientData.meals.filter(
                (m: any) => currentClientIdentity.id === m.clientIdentity.id && m.orderNumber === selectedOrderNumber
            );

            // Fonction pour générer les repas par défaut
            const generateDefaultMeals = () => {
                return ([] as EditedMeal[]).concat(
                    ...currentClient.clientIdentities.map(
                        (clientIdentity: any) =>
                            clientIdentity.defaultMeals
                                .map((defaultMeal: any) => {
                                    const date = weekDays.find(
                                        (d) =>
                                            moment(d).day() ===
                                            WEEK_DAY_MAP[defaultMeal.weekDay],
                                    );
                                    const dateStr =
                                        moment(date).format('YYYY-MM-DD');

                                    const holidays = getFrenchHolidays(new Date(dateStr).getFullYear());

                                    function isHoliday(date: any): boolean {
                                        return holidays.includes(moment(date).format('YYYY-MM-DD'));
                                    }

                                    function getDefaultDeliveryDay(columnDate: any, deliveryZoneId: any, clientIdentityDefaultMeals?: any) {
                                        let columnMoment = moment(columnDate);

                                        // Vérifier si nous avons des données de configuration pour ce jour
                                        if (clientIdentityDefaultMeals && Array.isArray(clientIdentityDefaultMeals)) {
                                            // Déterminer le jour de la semaine de columnDate (0-6, où 0 est dimanche dans moment.js)
                                            const dayOfWeek = columnMoment.day();

                                            // Convertir de format moment.js (dimanche = 0) à votre format (lundi = 0)
                                            const dayIndex = dayOfWeek === 0 ? 6 : dayOfWeek - 1;

                                            // Chercher dans le tableau une configuration pour ce jour de la semaine
                                            const weekdayNames = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
                                            const todayName = weekdayNames[dayIndex].toLowerCase();

                                            // Recherche de configuration pour le jour actuel
                                            const matchingConfig = clientIdentityDefaultMeals.find((item: any) => {
                                                // Vérifier si l'item a weekDay qui correspond au jour actuel
                                                return item &&
                                                    item.weekDay &&
                                                    item.weekDay.toLowerCase() === todayName &&
                                                    item.deliveryDay !== null;
                                            });

                                            // Si une configuration est trouvée, utiliser son deliveryDay
                                            if (matchingConfig && matchingConfig.deliveryDay !== undefined) {
                                                // Conversion de l'index deliveryDay (lundi = 0) vers l'index moment.js (dimanche = 0)
                                                const targetDay = (matchingConfig.deliveryDay + 1) % 7;

                                                const currentDay = columnMoment.day();
                                                let daysToAdjust;

                                                // Calculer combien de jours on doit ajuster pour atteindre le jour cible
                                                if (currentDay === targetDay) {
                                                    // Si on est déjà au jour cible, on ne change pas de date
                                                    daysToAdjust = 0;
                                                } else if (currentDay > targetDay) {
                                                    // Si le jour cible est plus tôt dans la semaine
                                                    daysToAdjust = targetDay - currentDay;
                                                } else {
                                                    // Si le jour cible est plus tard dans la semaine, on recule d'une semaine
                                                    daysToAdjust = targetDay - currentDay - 7;
                                                }

                                                return columnMoment.add(daysToAdjust, 'days');
                                            }
                                        }

                                        // Si pas de deliveryDay trouvé ou valide, on applique les règles par défaut
                                        const dayOfWeek = columnMoment.day();

                                        // Cas pour les zones spéciales
                                        if (deliveryZoneId === 'b3b7b771-3cbb-471d-be99-8f7f417060c1') {
                                            if (dayOfWeek === 0) {
                                                columnMoment = columnMoment.subtract(2, 'days'); // Dimanche => Vendredi
                                            } else if (dayOfWeek === 6) {
                                                columnMoment = columnMoment.subtract(1, 'days'); // Samedi => Vendredi
                                            } else if (dayOfWeek === 4) {
                                                columnMoment = columnMoment.subtract(1, 'days'); // Jeudi => Mercredi
                                            } else if (dayOfWeek === 2) {
                                                columnMoment = columnMoment.subtract(1, 'days'); // Mardi => Lundi
                                            }
                                        } else if (deliveryZoneId === '7d36dda0-2f3d-410d-ab2e-e4b984f703dd') {
                                            if (dayOfWeek === 0) {
                                                columnMoment = columnMoment.subtract(1, 'days'); // Dimanche => Samedi
                                            } else if (dayOfWeek === 5) {
                                                columnMoment = columnMoment.subtract(1, 'days'); // Vendredi => Jeudi
                                            } else if (dayOfWeek === 3) {
                                                columnMoment = columnMoment.subtract(1, 'days'); // Mercredi => Mardi
                                            } else if (dayOfWeek === 1) {
                                                columnMoment = columnMoment.subtract(2, 'days'); // Lundi => Samedi
                                            }
                                        } else {
                                            // Règles générales
                                            if (dayOfWeek === 0) {
                                                columnMoment = columnMoment.subtract(2, 'days'); // Dimanche => Vendredi
                                            } else if (dayOfWeek === 6) {
                                                columnMoment = columnMoment.subtract(1, 'days'); // Samedi => Vendredi
                                            }
                                        }

                                        return columnMoment;

                                    }

                                    const deliveryDateDefault = getDefaultDeliveryDay(date, currentClient.defaultDeliveryZone.id, clientIdentity?.defaultMeals).format('YYYY-MM-DD');

                                    clientData.clientIdentityAbsences.filter(
                                        (a: any) =>
                                            a.clientIdentity.id ===
                                            clientIdentity.id,
                                    );

                                    if (
                                        clientData.clientIdentityAbsences.findIndex(
                                            (absenceDate: any) =>
                                                absenceDate.clientIdentity
                                                    .id === clientIdentity.id &&
                                                absenceDate.date === dateStr,
                                        ) > -1
                                    ) {
                                        return null;
                                    }

                                    const menu = weekData.mealMenus.find(
                                        (m: any) =>
                                            m.date === dateStr &&
                                            m.mealDefinition.id ===
                                            defaultMeal.meal.id,
                                    );
                                    if (!menu) {
                                        return null;
                                    }

                                    return {
                                        mealPeriod: defaultMeal.mealPeriod,
                                        date: dateStr,
                                        menu: menu.id,
                                        dishes: defaultMeal.clientIdentityDefaultMealContents
                                            .map((d: any) => ({
                                                dish: d.dish.id,
                                                quantity: d.quantity,
                                                dishType: d.dishType
                                            })),
                                        clientIdentity: clientIdentity.id,
                                        day: deliveryDateDefault,
                                        default: true
                                    };
                                })
                                .filter(
                                    (m: EditedMeal | null) => !!m,
                                ) as EditedMeal[],
                    ),
                );
            };

            if (
                !clientDataLoading &&
                weekData &&
                currentClient &&
                clientMeals.length === 0
            ) {
                setEditedMeals(generateDefaultMeals());
            } else {
                // Cas 2: Au moins un repas enregistré - combiner repas enregistrés + préférences pour les jours manquants
                setEditedMeals((prev) => {
                    const filteredPrev = prev.filter(
                        (editedMeal) =>
                            currentClient &&
                            currentClient.clientIdentities.findIndex(
                                (i: any) => i.id === editedMeal.clientIdentity && editedMeal.orderNumber === selectedOrderNumber,
                            ) > -1 &&
                            weekDaysStr.includes(editedMeal.date),
                    );

                    // Récupérer les repas enregistrés existants
                    const existingMeals = clientMeals
                        .filter(
                            (m: any) =>
                                m.dishes.length === 0 &&
                                filteredPrev.findIndex(
                                    (p) =>
                                        p.date === m.date &&
                                        p.mealPeriod === m.mealPeriod,
                                ) === -1,
                        )
                        .map((meal: any) => {
                            const clientIdentity =
                                currentClient.clientIdentities.find(
                                    (i: any) => i.id === meal.clientIdentity.id,
                                );
                            const menu = weekData.mealMenus.find(
                                (m: any) => m.id === meal.menu.id,
                            );
                            const weekDay = Object.keys(WEEK_DAY_MAP).find(
                                (d) => WEEK_DAY_MAP[d] === moment(meal.date).day(),
                            );

                            if (!clientIdentity || !menu || !weekDay) {
                                return null;
                            }

                            const dishes = getDefaultDishes(
                                clientIdentity,
                                weekDay,
                                meal.mealPeriod,
                                menu,
                            );

                            return {
                                ...meal,
                                clientIdentity: meal.clientIdentity.id,
                                menu: meal.menu.id,
                                command: meal.command.id,
                                dishes: dishes,
                                day: meal.day,
                                default: false
                            };
                        })
                        .filter((m: any) => !!m) as EditedMeal[];

                    // Générer les repas par défaut pour les jours/périodes non couverts
                    const defaultMeals = generateDefaultMeals();
                    const missingDefaultMeals = defaultMeals.filter(defaultMeal => {
                        // Vérifier si ce repas par défaut n'existe pas déjà dans les repas enregistrés
                        const existsInRegistered = clientMeals.some((registeredMeal: any) =>
                            registeredMeal.date === defaultMeal.date &&
                            registeredMeal.mealPeriod === defaultMeal.mealPeriod &&
                            registeredMeal.clientIdentity.id === defaultMeal.clientIdentity
                        );

                        // Vérifier si ce repas par défaut n'existe pas déjà dans filteredPrev
                        const existsInPrev = filteredPrev.some(prevMeal =>
                            prevMeal.date === defaultMeal.date &&
                            prevMeal.mealPeriod === defaultMeal.mealPeriod &&
                            prevMeal.clientIdentity === defaultMeal.clientIdentity
                        );

                        return !existsInRegistered && !existsInPrev;
                    });

                    return filteredPrev.concat(existingMeals).concat(missingDefaultMeals);
                });
            }
        },
        [data, clientData, weekData, weekDays, currentClient, saveDraftLoading, saveCommandLoading, selectedOrderNumber]);


    useEffect(() => {
        if (!weekData || !dataClientSupplement) {
            return;
        }
        refetchClientSupplement();

        let updatedWeekData = JSON.parse(JSON.stringify(weekData));
        updatedWeekData.mealMenus.forEach((menu: any) => {
            dataClientSupplement.user.clientSupplements.forEach((supplement: any) => {
                if (!menu.dishes.some((dish: any) => dish.dish.id === supplement.dish.id)) {
                    menu.dishes.push({
                        id: supplement.dish.id,
                        dish: supplement.dish,
                        choiceNumber: null,
                        sortOrder: menu.dishes.length + 1
                    });
                }
            });
        });

        setLocalWeekData(updatedWeekData);
    }, [weekData, dataClientSupplement]);

    if (authError) {
        return (
            <div className="menu-client-error">
                <AlertTag
                    message={`Accès refusé : ${authError}. Veuillez contacter La cigale.`}
                    type="warning"
                />
            </div>
        );
    }

    if (!data || !clientData || !weekData) {
        return <LoaderOverlay loading/>;
    }
    if (error || clientDataError || weekDataError) {
        return <div>Erreur...</div>;
    }

    const currentClientMeals = currentClient
        ? clientData.meals.filter(
            (m: any) =>
                currentClient.clientIdentities.findIndex(
                    (c: any) => c.id === m.clientIdentity.id,
                ) > -1,
        )
        : [];
    const currentMeals = clientData.meals.filter(
        (m: any) => m.clientIdentity.id === selectedClientIdentityId ? selectedClientIdentityId : currentClientIdentity.id
    );


    const currentCommand =
        currentMeals.length > 0 ? currentMeals[0].command : null;

    function saveCommand(andOrder?: boolean) {
        return new Promise<void>((resolve, reject) => {
            if (!currentClient) {
                return reject();
            }

            const cleanedEditedMeals = editedMeals.map((e) => {

                const holidays = getFrenchHolidays(new Date(e.date).getFullYear());

                function isHoliday(date: any): boolean {
                    return holidays.includes(date);
                }

                const hasAlerts = alerts.some(a =>
                    a.meal.date === e.date &&
                    a.meal.mealPeriod === e.mealPeriod
                );

                const isSuccess = !hasAlerts && ((e.default !== true) ||  (e.default == true && e.mealPeriod === 'evening' && e.dishes.length >= 2 && e.dishes.some((d: any) => d.dishType=== 'dish' )));

                function getDefaultDeliveryDay(columnDate: any, deliveryZoneId: any) {
                    let columnMoment = moment(columnDate);
                    const dayOfWeek = columnMoment.day();

                    // Cas pour les zones spéciales
                    if (deliveryZoneId === 'b3b7b771-3cbb-471d-be99-8f7f417060c1') {
                        if (dayOfWeek === 0) {
                            columnMoment = columnMoment.subtract(2, 'days'); // Dimanche => Samedi
                        } else if (dayOfWeek === 6) {
                            columnMoment = columnMoment.subtract(1, 'days'); // Dimanche => Samedi
                        } else if (dayOfWeek === 4) {
                            columnMoment = columnMoment.subtract(1, 'days'); // Dimanche => Samedi
                        } else if (dayOfWeek === 2) {
                            columnMoment = columnMoment.subtract(1, 'days'); // Dimanche => Samedi
                        }
                    } else if (deliveryZoneId === '7d36dda0-2f3d-410d-ab2e-e4b984f703dd') {
                        if (dayOfWeek === 0) {
                            columnMoment = columnMoment.subtract(1, 'days'); // Dimanche => Samedi
                        } else if (dayOfWeek === 5) {
                            columnMoment = columnMoment.subtract(1, 'days'); // Dimanche => Samedi
                        } else if (dayOfWeek === 3) {
                            columnMoment = columnMoment.subtract(1, 'days'); // Dimanche => Samedi
                        } else if (dayOfWeek === 1) {
                            columnMoment = columnMoment.subtract(2, 'days'); // Dimanche => Samedi
                        }
                    } else {
                        // Règles générales
                        if (dayOfWeek === 0) {
                            columnMoment = columnMoment.subtract(2, 'days'); // Dimanche => Vendredi
                        } else if (dayOfWeek === 6) {
                            columnMoment = columnMoment.subtract(1, 'days'); // Samedi => Vendredi
                        }
                    }

                    return columnMoment;
                }

                const deliveryDateDefault = getDefaultDeliveryDay(e.date, currentClient.defaultDeliveryZone.id).format('YYYY-MM-DD');

                // Si ce n'est pas un "evening", on garde les valeurs normales
                return {
                    ...e,
                    price: undefined,
                    __typename: undefined,
                    default: undefined,
                    dishes: e.dishes.map(({dish, quantity}) => ({dish, quantity})),
                    orderNumber: 1,
                    day: e.day ? e.day : deliveryDateDefault,
                    success: isSuccess
                };
            }).filter(meal => (meal.success || (!meal.success && meal.id != null)));

            if (!currentCommand) {
                createCommand({
                    variables: {
                        input: {
                            client: currentClient.id,
                        },
                    },
                })
                    .then((res: any) => {
                        if (res.data) {
                            saveMeals({
                                variables: {
                                    command: res.data.createCommand.id,
                                    meals: cleanedEditedMeals,
                                },
                            })
                                .then(() => {
                                    if (andOrder) {
                                        orderCommand({
                                            variables: {
                                                id: res.data.createCommand.id,
                                            },
                                        })
                                            .then(() => resolve())
                                            .catch(reject);
                                    } else {
                                        resolve();
                                    }
                                }).then(() => {
                                if(menuWeekClientInfos){
                                    updateMenuWeekClient({
                                        variables: {
                                            id: menuWeekClientInfos.id,
                                            input: {

                                            },
                                        },
                                    })
                                }
                            }).then(() => {
                                if(menuWeekClientInfos) {
                                    createMealDishClient({
                                        variables: {
                                            menuWeek: menuWeekClientInfos.id,
                                            startDate,
                                            endDate,
                                            clientId: currentClient.id
                                        },
                                    })
                                }
                            }).then(() => {
                                    sendEmailMenuValidation({
                                        variables: {
                                            id: currentClient.id,
                                            week: weekNumber,
                                            year: year!= newYear ? newYear : year
                                        },
                                    })
                            }).catch(reject);
                        } else {
                            reject(reject);
                        }
                    })
                    .catch(reject);
            } else {
                saveMeals({
                    variables: {
                        command: currentCommand.id,
                        meals: cleanedEditedMeals,
                    },
                }).then(() => {
                    sendEmailMenuValidation({
                        variables: {
                            id: currentClient.id,
                            week: weekNumber,
                            year: year!= newYear ? newYear : year
                        },
                    })
                }).then(() => {
                    if(menuWeekClientInfos){
                        updateMenuWeekClient({
                            variables: {
                                id: menuWeekClientInfos.id,
                                input: {

                                },
                            },
                        })
                    }
                }).then(() => {
                    if(menuWeekClientInfos) {
                        createMealDishClient({
                            variables: {
                                menuWeek: menuWeekClientInfos.id,
                                startDate,
                                endDate,
                                clientId: currentClient.id
                            },
                        })
                    }
                }).then(() => {
                        if (andOrder) {
                            orderCommand({
                                variables: {
                                    id: currentCommand.id,
                                },
                            })
                                .then(() => resolve())
                                .catch(reject);
                        } else {
                            resolve();
                        }
                    })
                    .catch(reject);
            }
        });
    }

    const clientAlerts = getMealsAlerts({
        client: currentClient,
        editedMeals: editedMeals,
        currentMeals: currentClientMeals,
        mealMenus: weekData.mealMenus,
    });

    const mealEmptyAlert = areAllMealsEmpty({
        client: currentClient,
        editedMeals: editedMeals,
        currentMeals: currentClientMeals,
        mealMenus: weekData.mealMenus,
    });

    const alerts = clientAlerts;

    const mealOptions = () => {
        const clientMealsOrder = clientData.meals.filter(
            (m: any) =>
                currentClient.clientIdentities.findIndex(
                    (i: any) => i.id === m.clientIdentity.id && m.orderNumber === selectedOrderNumber,
                ) > -1,
        );

        const clientMeals = clientData.meals.filter(
            (m: any) =>
                currentClient.clientIdentities.findIndex(
                    (i: any) => i.id === m.clientIdentity.id,
                ) > -1,
        );

        if (!clientMealsOrder || clientMealsOrder.length === 0) {
            // Si la liste des meals est vide, retourne l'option par défaut + option de créer un nouveau menu
            if (selectedOrderNumber && selectedOrderNumber > 1) {


                const groupedOptions = clientMeals.reduce((acc: any, meal: any) => {
                    if (meal && meal.orderNumber !== null && meal.orderNumber !== undefined
                        && !acc.find((m: any) => m.value === meal.orderNumber)) {
                        acc.push({label: `Menu ${meal.orderNumber}`, value: meal.orderNumber});
                    }
                    return acc;
                }, []);

                // Ajouter l'option "Créer un nouveau menu" à la fin
                return [
                    ...groupedOptions,
                ];
            }
        } else {
            // Sinon, afficher les autres options groupées par orderNumber, avec l'option "Créer un nouveau menu"
            const groupedOptions = clientMeals.reduce((acc: any, meal: any) => {
                if (meal && meal.orderNumber !== null && meal.orderNumber !== undefined
                    && !acc.find((m: any) => m.value === meal.orderNumber)) {
                    acc.push({label: `Menu ${meal.orderNumber}`, value: meal.orderNumber});
                }
                return acc.sort((a: any, b: any) => a.value - b.value);
            }, []);

            // Ajouter l'option "Créer un nouveau menu" à la fin
            return [
                ...groupedOptions,
                {label: 'Créer un nouveau menu', value: 'newOrderNumber'}
            ];
        }
    };

    function onFilterChange(field: any, value: any, force?: boolean) {
        if (!data) {
            return;
        }

        if (navigationDisabled && field !== 'clientIdentity' && !force) {
            Modal.confirm({
                title: 'Voulez-vous quitter la page ?',
                content: 'Les données saisies ne seront pas enregistrées.',
                onOk() {
                    setEditedMeals([]);
                    onFilterChange(field, value, true);
                },
            });
            return;
        }

        switch (field) {
            case 'orderNumber':
                setMealOrderNumber(value);
                break;
        }
    }

    return (
        <div className="senior-commands-client">
            <div className="__banner">
                <div className="__logo">
                    <img className="__icon" src={logoIcon} alt="Logo La Cigale"/>
                    <img className="__text" src={logoText} alt="La Cigale"/>
                </div>
            </div>
            {/*<NavigationPrompt enabled={navigationDisabled}/>*/}

            <div className="__header">
                <h2>{`Saisie des repas de ${currentClient.firstName} ${currentClient.lastName}`}</h2>
                <WeekSelect
                    value={firstWeekDay}
                    startWeekDay={1}
                    modificationDays={true}
                    onChange={(v) => {
                        const newDateStr = moment(v).add(1, 'day').format('YYYY-MM-DD');
                        if (navigationDisabled) {
                            Modal.confirm({
                                title: 'Voulez-vous quitter la page ?',
                                content: 'Les données saisies ne seront pas enregistrées.',
                                onOk() {
                                    setFirstWeekDay(newDateStr);
                                },
                            });
                        } else {
                            setFirstWeekDay(newDateStr);
                        }

                        setMealOrderNumber(1);
                    }}
                />
            </div>

            <div className="__filters">
              <div className="__field">
                <div className="__label">Sélection du menu</div>
                    <Select
                        onChange={(value: any) => {
                            if (value === 'newOrderNumber') {
                                const clientMeals = clientData.meals.filter(
                                    (m: any) =>
                                        currentClient.clientIdentities.findIndex(
                                            (i: any) => i.id === m.clientIdentity.id
                                        ) > -1
                                );

                                const maxOrderNumber = Math.max(
                                    ...(
                                        clientMeals
                                            .map((meal: any) => meal.orderNumber)
                                            .filter((n) => n !== null && n !== undefined)
                                    ),
                                    0
                                );

                                const newOrderNumber = maxOrderNumber + 1;
                                onFilterChange('orderNumber', newOrderNumber);
                            } else {
                                onFilterChange('orderNumber', value);
                            }
                        }}
                        value={selectedOrderNumber ? `Menu ${selectedOrderNumber}` : undefined}
                        options={mealOptions()}
                        disabled
                    />
                </div>

                <div className="__field __actions">
                    <div className="__save-command-btn">
                        {currentClient && clientAlerts.length > 0 && (
                            <Popover
                                content={
                                    <div>
                                        {currentClient.clientIdentities.map((clientIdentity: any) => {
                                            const clientIdentityAlerts = clientAlerts
                                                .filter((a) => a.clientIdentity === clientIdentity.id)
                                                .filter(ArrayService.filterUniqueBy('message'));

                                            if (clientIdentityAlerts.length === 0) return null;

                                            return (
                                                <div key={clientIdentity.id}>
                                                    <h5>{`${clientIdentity.firstname} ${clientIdentity.lastname}`}</h5>
                                                    {clientIdentityAlerts.map((alert) => (
                                                        <AlertTag key={alert.message} message={alert.message}/>
                                                    ))}
                                                </div>
                                            );
                                        })}
                                    </div>
                                }
                            >
                                <div>
                                    <AlertTag message={`${clientAlerts.length} alertes`}/>
                                </div>
                            </Popover>
                        )}

                        <Button
                            type="primary"
                            className={`__validate-btn ${
                                !currentCommand &&
                                (editedMeals.length > 0 || currentMeals.length > 0) &&
                                alerts.length === 0 && !mealEmptyAlert
                                    ? '--success'
                                    : ''
                            }`}
                            shape="round"
                            disabled={alerts.length > 0 || mealEmptyAlert}
                            onClick={() => {
                                if (saveCommandLoading) return;

                                const handleSave = () => {
                                    setSaveCommandLoading(true);
                                    saveCommand(true)
                                        .then(() => {
                                            history.push({
                                                pathname: `/menu-client-used`,
                                            });
                                        })
                                        .catch((err) => {
                                            notification.error({
                                                message:
                                                    !currentCommand
                                                        ? 'Impossible de valider'
                                                        : 'Impossible de modifier',
                                            });
                                            setSaveCommandLoading(false);
                                        });
                                };
                                handleSave()
                            }}
                            loading={saveCommandLoading}

                        >
                            {!currentCommand
                                ? 'Valider la commande'
                                : 'Modifier la commande'}
                        </Button>
                    </div>
                </div>
            </div>

            <div className="__content">
                {clientDataLoading && <LoaderOverlay loading/>}
                <table className="__menu-table">
                    <colgroup>
                        {weekDays.map((date) => {
                            const dateStr = moment(date).format('YYYY-MM-DD');
                            const isAbsent =
                                absenceDates.findIndex(
                                    (d: any) => dateStr === d.format('YYYY-MM-DD')
                                ) > -1;

                            return (
                                <col
                                    key={dateStr}
                                    width={`${100 / weekDays.length}%`}
                                    style={isAbsent ? {background: '#F1F1F1'} : undefined}
                                />
                            );
                        })}
                    </colgroup>

                    <thead>
                    <tr>
                        {weekDays.map((date) => (
                            <th key={moment(date).format('YYMMDD')}>
                                <div>
                                    {moment(date).format('dddd').toUpperCase()} ({moment(date).format('DD')})
                                </div>
                            </th>
                        ))}
                    </tr>
                    </thead>

                    <tbody>
                    <TableAlertRow alerts={alerts} weekDays={weekDays} absenceDates={absenceDates}/>

                    <TablePeriodRow
                        period="noon"
                        client={currentClient}
                        clientIdentity={currentClientIdentity}
                        orderNumber={selectedOrderNumber}
                        dishTypes={data.dishTypes}
                        weekDays={weekDays}
                        mealMenus={weekData.mealMenus}
                        editedMeals={editedMeals}
                        meals={currentMeals}
                        onChange={setEditedMeals}
                        alerts={alerts}
                        localWeekData={localWeekData}
                        clientIdentityAbsences={clientData.clientIdentityAbsences}
                    />

                    <TablePeriodRow
                        period="evening"
                        client={currentClient}
                        clientIdentity={currentClientIdentity}
                        orderNumber={selectedOrderNumber}
                        dishTypes={data.dishTypes}
                        weekDays={weekDays}
                        mealMenus={weekData.mealMenus}
                        editedMeals={editedMeals}
                        meals={currentMeals}
                        onChange={setEditedMeals}
                        alerts={alerts}
                        localWeekData={localWeekData}
                        clientIdentityAbsences={clientData.clientIdentityAbsences}
                    />
                    </tbody>
                </table>
            </div>
        </div>
    );
};

interface TableAlertRowProps {
    alerts: MealAlert[];
    weekDays: Date[];
    absenceDates: moment.Moment[];
}

const TableAlertRow: React.FunctionComponent<TableAlertRowProps> = ({
                                                                        weekDays,
                                                                        alerts,
                                                                        absenceDates,
                                                                    }) => {
    if (alerts.length === 0 && absenceDates.length === 0) {
        return null;
    }
    return (
        <tr className="__table-alert-row">
            {weekDays.map((date) => {
                const dateStr = moment(date).format('YYYY-MM-DD');
                const dateAlerts = alerts.filter(
                    (a) => a.meal.date === dateStr,
                );
                return (
                    <td key={dateStr}>
                        {absenceDates.findIndex(
                            (absenceDate) =>
                                absenceDate.format('YYYY-MM-DD') === dateStr,
                        ) > -1 && (
                            <AlertTag message="Déclaré absent" type="info"/>
                        )}
                        {dateAlerts.map((alert) => (
                            <AlertTag
                                key={
                                    alert.meal.id +
                                    alert.meal.mealPeriod +
                                    alert.message
                                }
                                message={alert.message}
                            />
                        ))}
                    </td>
                );
            })}
        </tr>
    );
};

interface TablePeriodRowProps {
    client: Client,
    clientIdentity?: ClientIdentity;
    orderNumber: number;
    weekDays: Date[];
    dishTypes: DishType[];
    period: string;
    mealMenus: MealMenu[];
    editedMeals: EditedMeal[];
    meals: Meal[];
    onChange: (editedMeals: EditedMeal[]) => void;
    alerts: MealAlert[];
    localWeekData: WeekData | null;
    clientIdentityAbsences?: { date: string; clientIdentity: { id: string } }[];
}

const TablePeriodRow: React.FunctionComponent<TablePeriodRowProps> = ({
                                                                          client,
                                                                          clientIdentity,
                                                                          orderNumber,
                                                                          weekDays,
                                                                          dishTypes,
                                                                          period,
                                                                          mealMenus,
                                                                          editedMeals,
                                                                          meals,
                                                                          onChange,
                                                                          alerts,
                                                                          localWeekData,
                                                                          clientIdentityAbsences
                                                                      }) => {

    function handleChange(newMeal: EditedMeal) {

        // Blocage : impossible d'enregistrer un repas sur un jour où le
        // bénéficiaire est déclaré absent.
        const isAbsentDay = (clientIdentityAbsences || []).some(
            (a) =>
                clientIdentity &&
                a.clientIdentity.id === clientIdentity.id &&
                a.date === newMeal.date,
        );
        if (isAbsentDay) {
            notification.warning({
                message: 'Jour en absence',
                description:
                    'Impossible d’enregistrer un repas : le bénéficiaire est déclaré absent ce jour-là.',
            });
            return null;
        }

        const iMeal = editedMeals.findIndex(
            (m: any) =>
                clientIdentity &&
                m.clientIdentity === clientIdentity.id &&
                m.mealPeriod === newMeal.mealPeriod &&
                m.date === newMeal.date,
        );

        const newEditedMeals = [...editedMeals];
        if (iMeal > -1) {
            newEditedMeals[iMeal] = newMeal;
        } else {
            newEditedMeals.push(newMeal);
        }

        onChange(newEditedMeals);

    }

    const weekDaysStr = weekDays.map((date: any) =>
        moment(date).format('YYYY-MM-DD'),
    );

    let mealByDates: { [date: string]: EditedMeal | null } = {};
    let menuByDates: { [date: string]: MealMenu | undefined } = {};
    weekDaysStr.forEach((date: any) => {
        const editedMeal = editedMeals.find(
            (m: any) =>
                clientIdentity &&
                m.clientIdentity === clientIdentity.id &&
                m.mealPeriod === period &&
                m.date === date,
        );
        const existingMeal = meals.find(
            (m: any) =>
                clientIdentity &&
                m.clientIdentity.id === clientIdentity.id &&
                m.mealPeriod === period &&
                m.date === date && m.orderNumber === orderNumber,
        );

        const meal: EditedMeal | null =
            editedMeal ||
            (existingMeal
                ? {
                    ...existingMeal,
                    command: existingMeal.command.id,
                    menu: existingMeal.menu.id,
                    clientIdentity: existingMeal.clientIdentity.id,
                    orderNumber: orderNumber,
                    dishes: existingMeal.dishes.map((d: any) => ({
                        dish: d.dish.id,
                        quantity: d.quantity,
                        dishType: d.dishType
                    })),
                }
                : null);
        const menu = meal
            ? mealMenus.find((m: any) => m.id === meal.menu)
            : undefined;

        mealByDates[date] = meal;
        menuByDates[date] = menu;
    });

    const usedDishTypes = dishTypes.filter((dishType: any) => {
        return (
            Object.values(menuByDates).findIndex((menu) => {
                return menu?.dishTypes.includes(dishType.code);
            }) > -1
        );
    });

    return (
        <>
            <tr>
                {weekDaysStr.map((date) => (
                    <td key={date}>
                        <PeriodTag period={period} line/>
                    </td>
                ))}
            </tr>
            <tr>
                {weekDaysStr.map((date) => {
                    const dateMenus = mealMenus.filter(
                        (m: any) =>
                            m.date === date &&
                            m.mealDefinition.mealPeriods.includes(period),
                    );

                    const meal = mealByDates[date];
                    const menu = menuByDates[date];

                    function getFrenchHolidays(year: number): string[] {
                        // Jour de Pâques (basé sur le calcul de la date de Pâques)
                        const easter = moment(calculateEaster(year));

                        return [
                            moment(`${year}-01-01`).format("YYYY-MM-DD"), // Nouvel An
                            //easter.add(1, "days").format("YYYY-MM-DD"),  // Lundi de Pâques
                            moment(`${year}-05-01`).format("YYYY-MM-DD"), // Fête du Travail
                            moment(`${year}-05-08`).format("YYYY-MM-DD"), // Victoire 1945
                            easter.add(38, "days").format("YYYY-MM-DD"), // Ascension
                            easter.add(49, "days").format("YYYY-MM-DD"), // Pentecôte
                            easter.add(50, "days").format("YYYY-MM-DD"), // Lundi de Pentecôte
                            moment(`${year}-07-14`).format("YYYY-MM-DD"), // Fête Nationale
                            moment(`${year}-08-15`).format("YYYY-MM-DD"), // Assomption
                            moment(`${year}-11-01`).format("YYYY-MM-DD"), // Toussaint
                            moment(`${year}-11-11`).format("YYYY-MM-DD"), // Armistice 1918
                            moment(`${year}-12-25`).format("YYYY-MM-DD"), // Noël
                        ];
                    }

                    // Fonction pour calculer la date de Pâques (algorithme de Gauss simplifié)
                    function calculateEaster(year: number): string {
                        const a = year % 19;
                        const b = Math.floor(year / 100);
                        const c = year % 100;
                        const d = Math.floor(b / 4);
                        const e = b % 4;
                        const f = Math.floor((b + 8) / 25);
                        const g = Math.floor((b - f + 1) / 3);
                        const h = (19 * a + b - d - g + 15) % 30;
                        const i = Math.floor(c / 4);
                        const k = c % 4;
                        const l = (32 + 2 * e + 2 * i - h - k) % 7;
                        const m = Math.floor((a + 11 * h + 22 * l) / 451);
                        const month = Math.floor((h + l - 7 * m + 114) / 31);
                        const day = ((h + l - 7 * m + 114) % 31) + 1;

                        return `${year}-${month}-${day}`;
                    }

                    const holidays = getFrenchHolidays(new Date(date).getFullYear());

                    function isHoliday(date: any): boolean {
                        return holidays.includes(moment(date).format('YYYY-MM-DD'));
                    }

                    function getDefaultDeliveryDay(columnDate: any, deliveryZoneId: any, clientIdentityDefaultMeals?: any) {
                        let columnMoment = moment(columnDate);

                        // Vérifier si nous avons des données de configuration pour ce jour
                        if (clientIdentityDefaultMeals && Array.isArray(clientIdentityDefaultMeals)) {
                            // Déterminer le jour de la semaine de columnDate (0-6, où 0 est dimanche dans moment.js)
                            const dayOfWeek = columnMoment.day();

                            // Convertir de format moment.js (dimanche = 0) à votre format (lundi = 0)
                            const dayIndex = dayOfWeek === 0 ? 6 : dayOfWeek - 1;

                            // Chercher dans le tableau une configuration pour ce jour de la semaine
                            const weekdayNames = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
                            const todayName = weekdayNames[dayIndex].toLowerCase();

                            // Recherche de configuration pour le jour actuel
                            const matchingConfig = clientIdentityDefaultMeals.find((item: any) => {
                                // Vérifier si l'item a weekDay qui correspond au jour actuel
                                return item &&
                                    item.weekDay &&
                                    item.weekDay.toLowerCase() === todayName &&
                                    item.deliveryDay !== null;
                            });

                            // Si une configuration est trouvée, utiliser son deliveryDay
                            if (matchingConfig && matchingConfig.deliveryDay !== undefined) {
                                // Conversion de l'index deliveryDay (lundi = 0) vers l'index moment.js (dimanche = 0)
                                const targetDay = (matchingConfig.deliveryDay + 1) % 7;

                                const currentDay = columnMoment.day();
                                let daysToAdjust;

                                // Calculer combien de jours on doit ajuster pour atteindre le jour cible
                                if (currentDay === targetDay) {
                                    // Si on est déjà au jour cible, on ne change pas de date
                                    daysToAdjust = 0;
                                } else if (currentDay > targetDay) {
                                    // Si le jour cible est plus tôt dans la semaine
                                    daysToAdjust = targetDay - currentDay;
                                } else {
                                    // Si le jour cible est plus tard dans la semaine, on recule d'une semaine
                                    daysToAdjust = targetDay - currentDay - 7;
                                }

                                return columnMoment.add(daysToAdjust, 'days');
                            }
                        }

                        // Si pas de deliveryDay trouvé ou valide, on applique les règles par défaut
                        const dayOfWeek = columnMoment.day();

                        // Cas pour les zones spéciales
                        if (deliveryZoneId === 'b3b7b771-3cbb-471d-be99-8f7f417060c1') {
                            if (dayOfWeek === 0) {
                                columnMoment = columnMoment.subtract(2, 'days'); // Dimanche => Vendredi
                            } else if (dayOfWeek === 6) {
                                columnMoment = columnMoment.subtract(1, 'days'); // Samedi => Vendredi
                            } else if (dayOfWeek === 4) {
                                columnMoment = columnMoment.subtract(1, 'days'); // Jeudi => Mercredi
                            } else if (dayOfWeek === 2) {
                                columnMoment = columnMoment.subtract(1, 'days'); // Mardi => Lundi
                            }
                        } else if (deliveryZoneId === '7d36dda0-2f3d-410d-ab2e-e4b984f703dd') {
                            if (dayOfWeek === 0) {
                                columnMoment = columnMoment.subtract(1, 'days'); // Dimanche => Samedi
                            } else if (dayOfWeek === 5) {
                                columnMoment = columnMoment.subtract(1, 'days'); // Vendredi => Jeudi
                            } else if (dayOfWeek === 3) {
                                columnMoment = columnMoment.subtract(1, 'days'); // Mercredi => Mardi
                            } else if (dayOfWeek === 1) {
                                columnMoment = columnMoment.subtract(2, 'days'); // Lundi => Samedi
                            }
                        } else {
                            // Règles générales
                            if (dayOfWeek === 0) {
                                columnMoment = columnMoment.subtract(2, 'days'); // Dimanche => Vendredi
                            } else if (dayOfWeek === 6) {
                                columnMoment = columnMoment.subtract(1, 'days'); // Samedi => Vendredi
                            }
                        }

                        return columnMoment;

                    }

                    function isDisabledDate(current: any, columnDate: any, deliveryZoneId: any) {
                        const columnMoment = moment(columnDate);
                        const dayOfWeek = columnMoment.day(); // Jour de la semaine (0 = dimanche, 1 = lundi, etc.)

                        if (deliveryZoneId === 'b3b7b771-3cbb-471d-be99-8f7f417060c1') {
                            switch (dayOfWeek) {
                                case 5: // Vendredi
                                    return (
                                        current &&
                                        (current < columnMoment.subtract(2, 'days').startOf('day') || // 1 jours avant
                                            current > columnMoment.add(2, 'days').endOf('day')) // Pas de jours après
                                    );

                                case 6: // Samedi
                                    return (
                                        current &&
                                        (current < columnMoment.subtract(3, 'days').startOf('day') || // 1 jours avant
                                            current > columnMoment.add(2, 'days').endOf('day')) // Pas de jours après
                                    );

                                case 0: // Dimanche

                                    return (
                                        current &&
                                        (current < columnMoment.subtract(4, 'days').startOf('day') || // 1 jours avant
                                            current > columnMoment.add(2, 'days').endOf('day')) // Pas de jours après
                                    );

                                case 1: // Lundi
                                    return !current.isSame(columnMoment, 'day'); // Pas de jours avant, que les jours après

                                case 2: // Mardi
                                    return (
                                        current &&
                                        (current < columnMoment.subtract(2, 'days').startOf('day') || // 1 jours avant
                                            current > columnMoment.add(0, 'days').endOf('day'))
                                    );

                                case 3: // Mercredi
                                    return (
                                        current &&
                                        (current < columnMoment.subtract(2, 'days').startOf('day') || // 1 jours avant
                                            current > columnMoment.add(2, 'days').endOf('day')) // 2 jours après
                                    );

                                case 4: // Jeudi
                                    return (
                                        current &&
                                        (current < columnMoment.subtract(3, 'days').startOf('day') || // 1 jours avant
                                            current > columnMoment.add(2, 'days').endOf('day')) // 1 jour après
                                    );

                                default:
                                    return false; // Aucun autre jour n'est concerné
                            }
                        }else if (deliveryZoneId === '7d36dda0-2f3d-410d-ab2e-e4b984f703dd') {
                            switch (dayOfWeek) {
                                case 5: // Vendredi
                                    return (
                                        current &&
                                        (current < columnMoment.subtract(3, 'days').startOf('day') || // 1 jours avant
                                            current > columnMoment.add(2, 'days').endOf('day')) // Pas de jours après
                                    );

                                case 6: // Samedi
                                    return (
                                        current &&
                                        (current < columnMoment.subtract(2, 'days').startOf('day') || // 1 jours avant
                                            current > columnMoment.add(2, 'days').endOf('day')) // Pas de jours après
                                    );

                                case 0: // Dimanche

                                    return (
                                        current &&
                                        (current < columnMoment.subtract(3, 'days').startOf('day') || // 1 jours avant
                                            current > columnMoment.add(2, 'days').endOf('day')) // Pas de jours après
                                    );

                                case 1:
                                    return (
                                        current &&
                                        (current < columnMoment.subtract(4, 'days').startOf('day') || // 1 jours avant
                                            current > columnMoment.add(2, 'days').endOf('day')) // Pas de jours après
                                    );

                                case 2: // Mardi
                                    return (
                                        current &&
                                        (current < columnMoment.subtract(1, 'days').startOf('day') || // 1 jours avant
                                            current > columnMoment.add(1, 'days').endOf('day'))
                                    );

                                case 3: // Mercredi
                                    return (
                                        current &&
                                        (current < columnMoment.subtract(2, 'days').startOf('day') || // 1 jours avant
                                            current > columnMoment.add(1, 'days').endOf('day')) // 2 jours après
                                    );

                                case 4: // Jeudi
                                    return (
                                        current &&
                                        (current < columnMoment.subtract(2, 'days').startOf('day') || // 1 jours avant
                                            current > columnMoment.add(2, 'days').endOf('day')) // 1 jour après
                                    );

                                default:
                                    return false; // Aucun autre jour n'est concerné
                            }
                        }else {
                            switch (dayOfWeek) {
                                case 5: // Vendredi
                                    return (
                                        current &&
                                        (current < columnMoment.subtract(2, 'days').startOf('day') || // 1 jours avant
                                            current > columnMoment.add(2, 'days').endOf('day')) // Pas de jours après
                                    );

                                case 6: // Samedi
                                    return (
                                        current &&
                                        (current < columnMoment.subtract(3, 'days').startOf('day') || // 1 jours avant
                                            current > columnMoment.add(2, 'days').endOf('day')) // Pas de jours après
                                    );

                                case 0: // Dimanche

                                    return (
                                        current &&
                                        (current < columnMoment.subtract(4, 'days').startOf('day') || // 1 jours avant
                                            current > columnMoment.add(2, 'days').endOf('day')) // Pas de jours après
                                    );

                                case 1: // Lundi
                                    return !current.isSame(columnMoment, 'day'); // Pas de jours avant, que les jours après

                                case 2: // Mardi
                                    return (
                                        current &&
                                        (current < columnMoment.subtract(1, 'days').startOf('day') || // 1 jours avant
                                            current > columnMoment.add(1, 'days').endOf('day'))
                                    );

                                case 3: // Mercredi
                                    return (
                                        current &&
                                        (current < columnMoment.subtract(2, 'days').startOf('day') || // 1 jours avant
                                            current > columnMoment.add(2, 'days').endOf('day')) // 2 jours après
                                    );

                                case 4: // Jeudi
                                    return (
                                        current &&
                                        (current < columnMoment.subtract(2, 'days').startOf('day') || // 1 jours avant
                                            current > columnMoment.add(2, 'days').endOf('day')) // 1 jour après
                                    );

                                default:
                                    return false; // Aucun autre jour n'est concerné
                            }
                        }
                    }

                    const clientDeliveryZone = client? client.defaultDeliveryZone?.id : null;
                    const deliveryDateDefault = getDefaultDeliveryDay(date, clientDeliveryZone, clientIdentity?.defaultMeals).format('YYYY-MM-DD');

                    return (
                        <td
                            key={date}
                        >
                            <div className="__delivery-day">
                                <DatePicker
                                    value={
                                        menu
                                            ? meal?.day
                                                ? moment(meal.day)
                                                : deliveryDateDefault
                                                    ? moment(deliveryDateDefault)
                                                    : getDefaultDeliveryDay(date, clientDeliveryZone)
                                            : undefined
                                    } // Utilise le jour par défaut si le menu est défini
                                    onChange={(v) => {
                                        const newDate = v
                                            ? v.format('YYYY-MM-DD')
                                            : getDefaultDeliveryDay(date, clientDeliveryZone).format(
                                                'YYYY-MM-DD',
                                            );
                                        if (meal) {
                                            handleChange({
                                                ...meal,
                                                day: newDate,
                                            });
                                        }
                                    }}
                                    format="dddd DD MMMM YYYY"
                                    disabled
                                    disabledDate={(current) =>
                                        isDisabledDate(current, date, clientDeliveryZone)
                                    }
                                />
                            </div>
                            <Select
                                className="__menu-select"
                                allowClear
                                value={menu ? menu.id : undefined}

                                placeholder="Choisissez une formule"
                                options={[
                                    {
                                        label: 'Aucun',
                                        value: null as unknown as string,
                                    },
                                    ...dateMenus.map((m: any) => ({
                                        label: m.mealDefinition.name,
                                        value: m.id

                                    })),
                                ]}
                                onChange={(v: string | null) => {
                                    clientIdentity &&
                                    handleChange(
                                        meal
                                            ? {
                                                ...meal,
                                                menu: v,
                                                dishes: [],
                                                orderNumber: orderNumber,
                                                day: meal.day ? meal.day : deliveryDateDefault,
                                                default: false

                                            }
                                            : {
                                                mealPeriod: period,
                                                date,
                                                menu: v,
                                                dishes: [],
                                                clientIdentity:
                                                clientIdentity.id,
                                                orderNumber: orderNumber,
                                                day: '',
                                                default: false
                                            },
                                    );
                                }}
                                disabled
                            />
                        </td>
                    );
                })}
            </tr>
            {usedDishTypes.map((dishType: any) => {
                return (
                    <tr key={dishType.code}>
                        {weekDaysStr.map((date: any) => {
                            const meal = mealByDates[date];
                            const menu = menuByDates[date];
                            const dishes = menu
                                ? [
                                    ...menu.dishes
                                        .filter(d => d.dish.type === dishType.code)
                                        .sort(ArrayService.cmpBy(d => `${d.choiceNumber} ${d.dish.name}`))
                                        .map(d => d.dish),

                                    ...(localWeekData ? localWeekData.mealMenus.flatMap((menu: any) =>
                                        menu.dishes
                                            .filter((localDish: any) => localDish.dish.type === "extra" && dishType.code === "extra")
                                            .map((localDish: any) => localDish.dish)
                                    ) : [])
                                ]
                                    .filter(ArrayService.filterUniqueEntity, (dish: any) => dish.id)
                                : [];

                            const hasAlerts =
                                alerts.findIndex(
                                    (a: any) =>
                                        meal &&
                                        a.meal.date === meal.date &&
                                        a.meal.mealPeriod === meal.mealPeriod,
                                ) > -1;

                            const hiddenSideDish =
                                dishType.code === 'side-dish' &&
                                menu &&
                                meal &&
                                menu.dishes.findIndex(
                                    (d) =>
                                        d.dish.type === 'dish' &&
                                        d.dish.noSideDish &&
                                        meal.dishes.findIndex(
                                            (dish) => dish.dish === d.dish.id,
                                        ) > -1,
                                ) > -1;
                            const hiddenDishType =
                                menu && !menu.dishTypes.includes(dishType.code);

                            return (
                                <td
                                    key={date}
                                    className={`${
                                        !hasAlerts  && meal?.default != true ? '--success' : ''
                                    }`}
                                >
                                    {!hiddenDishType && (
                                        <h3 className="__dish-type">
                                            <span>{dishType.label}</span>
                                        </h3>
                                    )}
                                    {hiddenSideDish && (
                                        <div className="__hidden-msg">
                                            Aucun
                                        </div>
                                    )}
                                    {!hiddenDishType &&
                                        !hiddenSideDish &&
                                        clientIdentity &&
                                        menu &&
                                        meal && (
                                            <>
                                                <div
                                                    className="__choice"
                                                    style={{
                                                        minHeight:
                                                            dishType.code ===
                                                            'side-dish'
                                                                ? 95
                                                                : undefined,
                                                    }}
                                                >
                                                    {dishes.map((d) =>
                                                        (menu.mealDefinition.name != 'Complément du Soir' &&  clientIdentity.allowMultipleDishes &&
                                                            INCREMENTABLE_DISH_TYPES.includes(
                                                                dishType.code,
                                                            )) ||
                                                        dishType.code ===
                                                        'extra' ? (
                                                            <SelectMultipleDishButton
                                                                key={d.id}
                                                                dish={d}
                                                                meal={meal}
                                                                menu={menu}
                                                                code={dishType.code}
                                                                onChange={
                                                                    handleChange
                                                                }

                                                            />
                                                        ) : (
                                                            <SelectDishButton
                                                                key={d.id}
                                                                dish={d}
                                                                meal={meal}
                                                                dishes={dishes}
                                                                menu={menu}
                                                                onChange={
                                                                    handleChange
                                                                }
                                                            />
                                                        ),
                                                    )}
                                                </div>
                                                <AllergiesWarning
                                                    meal={meal}
                                                    dishes={dishes}
                                                    clientIdentity={
                                                        clientIdentity
                                                    }
                                                />
                                            </>
                                        )}
                                </td>
                            );
                        })}
                    </tr>
                );
            })}
        </>
    );
};

interface AllergiesWarningProps {
    dishes: Dish[];
    meal: EditedMeal;
    clientIdentity: ClientIdentity;
}

const AllergiesWarning: React.FunctionComponent<AllergiesWarningProps> = ({
                                                                              dishes,
                                                                              meal,
                                                                              clientIdentity,
                                                                          }) => {
    const selectedDish = dishes.find(
        (d) => meal.dishes.findIndex((dish) => dish.dish === d.id) > -1,
    );

    if (!selectedDish) {
        return null;
    }

    const allergies =
        selectedDish && clientIdentity
            ? selectedDish.allergies.filter(
                (a) =>
                    clientIdentity.allergies.findIndex(
                        (ca) => ca.id === a.id,
                    ) > -1,
            )
            : [];

    if (allergies.length === 0) {
        return null;
    }
    return (
        <AlertTag
            message={`Ce plat contient : ${allergies
                .map((a) => a.content)
                .join(', ')}`}
        />
    );
};

interface SelectMultipleDishButtonProps {
    dish: Dish;
    meal: EditedMeal;
    menu: MealMenu;
    code: string;
    onChange: (newMeal: EditedMeal) => void;
}

const SelectMultipleDishButton: React.FunctionComponent<SelectMultipleDishButtonProps> =
    ({dish, meal, menu, onChange,code}) => {
        const selectedQuantity =
            meal.dishes.find((d) => d.dish === dish.id)?.quantity || 0;
        const isSelected = selectedQuantity > 0;

        function add() {
            const iDish = meal.dishes.findIndex((d) => d.dish === dish.id);

            let newDishes: {
                dish: string;
                quantity: number;
            }[] = [...meal.dishes];
            if (iDish === -1) {
                newDishes.push({
                    dish: dish.id,
                    quantity: 1,
                });
            } else {
                newDishes[iDish].quantity += 1;
            }
            if (dish.type === 'dish' && dish.noSideDish) {
                newDishes = newDishes.filter(
                    (d) =>
                        menu.dishes.findIndex(
                            (md) =>
                                md.dish.type === 'side-dish' &&
                                md.dish.id === d.dish,
                        ) === -1,
                );
            }
            onChange({
                ...meal,
                default: false,
                dishes: newDishes,
            });
        }

        function subtract() {
            const iDish = meal.dishes.findIndex((d) => d.dish === dish.id);
            if (iDish === -1) {
                return;
            }

            const newDishes = [...meal.dishes];
            if (newDishes[iDish].quantity <= 1) {
                newDishes.splice(iDish, 1);
            } else {
                newDishes[iDish].quantity -= 1;
            }
            onChange({
                ...meal,
                default: false,
                dishes: newDishes,
            });
        }

        return (
            <div className="__select-multiple-dish-button">
                <Button type={isSelected ? 'primary' : undefined}
                        onClick={add}
                        disabled={code=='extra'}
                >
                    {dish.name}
                </Button>
                <div className="__actions">
                    <Button onClick={subtract} disabled={code=='extra'} >-</Button>
                    <div className="__counter">{selectedQuantity}</div>
                    <Button onClick={add} disabled={code=='extra'} >+</Button>
                </div>
            </div>
        );
    };

interface SelectDishButtonProps {
    dish: Dish;
    dishes: Dish[];
    meal: EditedMeal;
    menu: MealMenu;
    onChange: (newMeal: EditedMeal) => void;
}

const SelectDishButton: React.FunctionComponent<SelectDishButtonProps> = ({
                                                                              dish,
                                                                              meal,
                                                                              dishes,
                                                                              menu,
                                                                              onChange,
                                                                          }) => {
    const isSelected = meal.dishes.findIndex((d) => d.dish === dish.id) > -1;
    return (
        <Button
            type={isSelected ? 'primary' : undefined}
            onClick={() => {
                const iDish = meal.dishes.findIndex((d) => d.dish === dish.id);

                let newDishes: {
                    dish: string;
                    quantity: number;
                }[] = meal.dishes.filter(
                    (d) =>
                        dishes.findIndex((dish) => dish.id === d.dish) === -1,
                );
                if (iDish === -1) {
                    newDishes.push({
                        dish: dish.id,
                        quantity: 1,
                    });
                }
                if (dish.type === 'dish' && dish.noSideDish) {
                    newDishes = newDishes.filter(
                        (d) =>
                            menu.dishes.findIndex(
                                (md) =>
                                    md.dish.type === 'side-dish' &&
                                    md.dish.id === d.dish,
                            ) === -1,
                    );
                }

                onChange({
                    ...meal,
                    default: false,
                    dishes: newDishes,
                });
            }}
        >
            {dish.name}
        </Button>
    );
};

interface SelectWithButtonsProps {
    onChange?: (value: string) => void;
    value?: string;
    options?: {
        label: string | ReactElement;
        value: string;
        filterValue: string;
    }[];
    disabled?: boolean;
}

const SelectWithButtons: React.FunctionComponent<SelectWithButtonsProps> = ({
                                                                                onChange,
                                                                                value,
                                                                                options,
                                                                                disabled,
                                                                            }) => {
    function goToPrev() {
        if (!options || !onChange) {
            return;
        }
        const iOption = options.findIndex((o) => o.value === value);
        if (iOption > 0) {
            onChange(options[iOption - 1].value);
        }
    }

    function goToNext() {
        if (!options || !onChange) {
            return;
        }
        const iOption = options.findIndex((o) => o.value === value);
        if (iOption < options.length - 1) {
            onChange(options[iOption + 1].value);
        }
    }

    return (
        <div className="select-with-buttons">
            <Button
                icon={<UpOutlined/>}
                shape="circle"
                onClick={goToPrev}
                disabled={disabled}
            />
            <Select
                dropdownClassName="select-dropdown"
                onChange={onChange}
                value={value}
                options={options}
                disabled={disabled}
                showSearch
                optionFilterProp="label"
                filterOption={(input, option) => {
                    return (
                        option?.filterValue
                            ?.toLowerCase()
                            .indexOf(input.toLowerCase()) >= 0
                    );
                }}
            />
            <Button
                icon={<DownOutlined/>}
                shape="circle"
                onClick={goToNext}
                disabled={disabled}
            />
        </div>
    );
};